import React, { useEffect, useState } from "react";
import { FC } from 'react';
import Breadcrumb from "components/commercetools-ui/atoms/breadcrumb";
import Link from "components/commercetools-ui/atoms/link";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import { HomeIcon } from "@heroicons/react/24/outline";
import usePath from "helpers/hooks/usePath";
import Markdown from "components/commercetools-ui/organisms/markdown";
import Image from "frontastic/lib/image";
import Slider from "components/commercetools-ui/atoms/slider";
import useMediaQuery from "helpers/hooks/useMediaQuery";
//import { desktop, tablet } from "helpers/utils/screensizes";
import { StaticImageData } from "next/image";
import { mediumDesktop, desktop, mobile, tablet, smallMobile, hugeDesktop, largeDesktop, semiLargeDesktop} from 'helpers/utils/screensizes';
import { trackEvent, trackVideoProgress } from 'helpers/customEvents';

export type ContentstackRendererProps = {
    data?: any;
  };
const ContentstackRenderer: FC<ContentstackRendererProps> = ({ data }) => {

  const { path } = usePath();
  const [isClient, setIsClient] = useState(false);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isMobileSize] = useMediaQuery(smallMobile);
  const [isHugeDesktopSize] = useMediaQuery(hugeDesktop);
  const [isSemiLargeDesktopSize] = useMediaQuery(semiLargeDesktop);
  const [ismediumDesktopSize] = useMediaQuery(mediumDesktop);
  const [isLargeDesktopSize] = useMediaQuery(largeDesktop);
  const videoMilestones = React.useRef(new Set<number>());

  useEffect(() => {
    setIsClient(true); // Ensure client-side logic runs only after mounting
  }, []);

  if (!isClient) {
    return null; // Render nothing or a loading spinner until the component mounts
  }
  
  const isInnovation = path?.includes('innovation');
  // Generate breadcrumbs based on the path
  const generateBreadcrumbs = () => {
    const pathSegments = path.split("/").filter(Boolean);
    const href = `/${pathSegments.slice(0, 1).join("/")}/`;    
   // const pathSegmentsWithoutLast = pathSegments.slice(0, 1);
    if(isInnovation)
    {
      return (
        [
          <span key={1} className="text-14 font-normal capitalize leading-4 text-[#666]" style={{ cursor: "default" }}>About Us</span>,
          <span key={2} className="text-14 font-normal capitalize leading-4 text-[#666]" style={{ cursor: "default" }}>
            <Link link={href}>Our Innovations</Link>
          </span>
        ]
      );
    }

    return [
      /*<span
        key="static-media"
        className="text-12 font-normal capitalize leading-4 text-primary-black"
        style={{ cursor: "default" }}
      >
        Products
      </span>,*/
      ...pathSegments.map((segment, index) => {        
        const isLast = index === pathSegments.length - 1; // Check if it's the last segment
  

        return isLast ? (
          // Current page breadcrumb (Non-clickable)
          <span
            key={segment}
            className="text-12 font-normal capitalize leading-4 text-primary-black"
            style={{ cursor: "default" }}
          >
            {segment.replace(/-/g, " ")}
          </span>
        ) : (
          // Clickable breadcrumbs
          <span key={segment}>
            <Link link={href}>
              <span className="text-12 font-normal capitalize leading-4 text-[#666]">
                {segment.replace(/-/g, " ")}
              </span>
            </Link>
          </span>
        );
      }),
    ];
  };

  // Get the sections from the JSON data
  const sections = data?.dataSource?.sections || [];
  if (!sections.length) {
    return <div>No sections to display.</div>;
  }

  // Dynamically render sections based on their type
  return (
    <div className="sections-container">
      {/* Breadcrumb */}
      <div className="hidden px-24 pb-8 pt-16 md:!block md:px-28 lg:px-48 xl:px-60 2xl:pl-60 2xl:pr-80 3xl:pl-80">
        <Breadcrumb Separator={<ChevronRightIcon className="w-16" />} listClassName="!gap-x-16">
          <Link link={"/"}>
            <HomeIcon className="mt-6 w-20" />
          </Link>
          {generateBreadcrumbs()}
        </Breadcrumb>
      </div>

      {/* Dynamically render sections based on the JSON structure */}
      {sections.map((section: any, index: number) => {
        // Check if the section is of type `section_with_image_text`
        if (section.section_with_image_text) {
          const {
            title,
            description,
            image,
            desktop_image_alignment = "Left",
            mobile_image_alignment = "Top",
            ctas,
            additional_css,
          } = section.section_with_image_text;

          const desktopAlignmentClass =
            desktop_image_alignment === "Right" ? "lg:flex-row-reverse" : "lg:flex-row";
          const mobileAlignmentClass =
            mobile_image_alignment === "Bottom" ? "flex-col-reverse" : "flex-col";

            return (
                <div
                key={index}
                className={`image-text-section ${additional_css || ""} flex items-center gap-24 pb-24 ${desktopAlignmentClass} ${mobileAlignmentClass}`}
              >
                {/* Image Section */}
                {image?.url && (
                  <div className="relative w-full flex-1 flex-grow">
                    <Image
                      src={image.url}
                      alt={image.description || "Section Image"}
                      className="w-full object-cover h-[20rem] md:h-[46rem] lg:flex-1 lg:flex-grow xl:h-[32rem] middleresolution:h-[37rem] 2xl:h-[45rem]"
                    />
                  </div>
                )}
              
                {/* Content Section */}
                <div className="flex-1 flex-grow px-[20.5px] sm:px-24 lg:px-48 xl:px-60 3xl:px-80">
                  {title && <Markdown markdown={title} className="text-32 font-medium leading-10 mb-4 md:text-40 2xl:text-48" />}
                  {description && (
                    <div
                      className=" lg:p-16 xl:p-16 2xl:p-16 text-16 font-normal md:text-18 lg:leading-8 2xl:text-20 leading-6 text-[#333]"
                      style={{
                        textAlign: description.includes("style=\"text-align: center\"") ? "center" : "left",
                      }}
                    >
                      <Markdown markdown={description} 
                            className="[&>ul]:list-disc [&>ul]:ml-20 [&>ul>li]:my-4"/>
                    </div>
                  )}
              
                  {/* CTA Buttons */}
                  {ctas &&
                    ctas.map((cta: any, idx: number) => (
                      <div
                        className={`mt-20 flex ${
                          description.includes("style=\"text-align: center\"")
                            ? "justify-center"
                            : description.includes("style=\"text-align: right\"")
                            ? "justify-end"
                            : "justify-start"
                        }`}
                        key={idx}
                      >
                        <Link
                          link={cta.button.href}
                          target={cta.button.href.startsWith("http") ? "_blank" : ""}
                          rel={cta.button.href.startsWith("http") ? "noopener noreferrer" : ""}
                        >
                          <button
                            style={{
                              backgroundColor: cta.button_background_color?.hex,
                              color: cta.button_text_color?.hex,
                              border: `1px solid ${cta.button_border_color?.hex}`,
                              width: "100%", // Button width set to 60% of its container
                              minWidth: "150px", // Ensure a reasonable minimum width
                            }}
                            className="px-8 py-8 text-16"
                          >
                            {cta.button.title}
                          </button>
                        </Link>
                      </div>
                    ))}
                </div>
              </div>
              
              );
        }
// Check if the section is of type section_with_video
if (section.section_with_video) {
  const {
    title,
    description,
    content_alignment = "left",
    video_thumbnail,
    video_link,
    additional_css,
  } = section.section_with_video;

  const isYouTube =
    video_link?.href.includes("youtube.com") || video_link?.href.includes("youtu.be");

  // Determine layout classes and ordering based on content_alignment
  const isVerticalAlignment =
    content_alignment.toLowerCase() === "top" || content_alignment.toLowerCase() === "bottom";
  const isHorizontalAlignment =
    content_alignment.toLowerCase() === "left" || content_alignment.toLowerCase() === "right";

  const layoutClass = isVerticalAlignment ? "flex-col" : "flex-row";

  const contentOrder =
    content_alignment.toLowerCase() === "bottom" ||
    content_alignment.toLowerCase() === "right"
      ? "order-2"
      : "order-1";

  const videoOrder =
    content_alignment.toLowerCase() === "bottom" ||
    content_alignment.toLowerCase() === "right"
      ? "order-1"
      : "order-2";

  // Apply centered alignment for vertical layouts (top/bottom)
  const alignmentClass = isVerticalAlignment
    ? "items-center text-center"
    : "items-center text-left"; // Center vertically for horizontal layout

  return (
    <div
      key={index}
      className={`video-section flex ${layoutClass} ${alignmentClass} justify-between md:px-100 lg:py-85 2xl:px-208 ${additional_css || ""}`}
    >
      {/* Content Section */}
      {title || description ? (
        <div
          className={`content-container max-w-[500px] md:text-14 text-primary-black ${contentOrder}`}
        >
          {title && (
            <Markdown
              markdown={title}
              className={`text-xl font-bold mb-4 ${
                isVerticalAlignment ? "text-center" : "text-left"
              }`}
            />
          )}
          {description && (
            <div
              className="description whitespace-pre-wrap font-normal md:text-14 text-16 text-primary-black"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
        </div>
      ) : null}

     {/* Video Section */}
<div
  className={`video-container w-full bg-black ${
    isHorizontalAlignment ? 'max-w-[700px]' : 'max-w-full'
  } ${videoOrder}`}
  style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }}
>
  {isYouTube ? (
    <div
      className="youtube-wrapper"
      style={{
        position: 'relative',
        width: '100%',
        paddingBottom: '56.25%', // Maintain 16:9 aspect ratio
      }}
    >
      <iframe
        className="absolute top-0 left-0 w-full h-full"
        src={video_link.href.replace('watch?v=', 'embed/')}
        onLoad={() => trackEvent('video_start', { src: video_link.href, video_type: 'youtube' })}
        title={video_link.title || 'YouTube Video'}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{
          height:
            isHugeDesktopSize
              ? '720px'
              : isSemiLargeDesktopSize
              ? '480px'
              : ismediumDesktopSize
              ? '500px'
              : isLargeDesktopSize
              ? '600px'
              : '320px',
        }}
      />
    </div>
  ) : (
    <video
      controls
      poster={video_thumbnail?.url}
      className=" "
      onPlay={(e) => trackEvent('video_start', { src: e.currentTarget.currentSrc })}
      onTimeUpdate={(e) => trackVideoProgress(e, videoMilestones)}
      onEnded={(e) => trackEvent('video_complete', { src: e.currentTarget.currentSrc })}
      style={{
        height:
          isHugeDesktopSize
            ? '720px'
            : isSemiLargeDesktopSize
            ? '480px'
            : ismediumDesktopSize
            ? '500px'
            : isLargeDesktopSize
            ? '600px'
            : '320px',
      }}
    >
      <source src={video_link.href} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  )}
</div>
    </div>
  );
}


        
        // Check if the section is of type `section_with_heading_description`
if (section.section_with_heading_description) {
  const { title, description, additional_css } = section.section_with_heading_description;

  return (
    <div
      key={index}
      className={`sm:px-14 lg:px-48 xl:px-60 3xl:px-80 heading-description-section ${additional_css || ""}`}
    >
      {/* Title Section */}
      {title && (
        <div className="mb-8 mt-20 block text-32 md:text-40 2xl:text-48 font-medium leading-10 text-primary-black lg:leading-[3.25rem] text-center">
          <h2 dangerouslySetInnerHTML={{ __html: title }} />
        </div>
      )}

      {/* Description Section */}
      {description && (
        <div className="w-full lg:w-[1700px] max-w-[calc(100%-16px)] lg:max-w-[calc(100%-90px)] 2xl:max-w-[calc(100%-208px)] px-36 md:px-90 2xl:px-208 py-0 2xl:py-40 mx-auto markdown-container">
          <div
            className="[&>p]:pt-12 [&>ul]:w-[min(100%,47rem)] [&>ul]:m-auto [&>ol]:w-[min(100%,47rem)] [&>ol]:m-auto [&>figure>div>figcaption]:w-[min(100%,47rem)] [&>figure>div>figcaption]:m-auto [&>ul]:list-disc [&>ul>li]:my-4 [&>ul>li]:pl-4 [&>ul>li]:text-left [&>ul>li>p>a]:!text-[#337ab7] [&>p>a]:!text-[#337ab7] [&>a]:!text-[#337ab7]  lg:p-16 xl:p-16 2xl:p-16 text-16 font-normal md:text-18 lg:leading-8 2xl:text-20 leading-6 text-[#333]"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>
      )}
    </div>
  );
}

 // Check if the section is of type `section_with_columns`
if (section.section_with_columns) {
  const { section_with_cards, additional_classname } = section.section_with_columns;
  const columnsCount = additional_classname === "4sections" ? 4 : 3; // Decide column count
  return (
    <div
      key={index}
      className={`columns-section bg-[#F8F8F8] py-64 px-16 md:px-90 2xl:px-208 2xl:py-80 ${additional_classname || ''}`.trim()}
    >
    <div
        className={`grid gap-8 sm:grid-cols-3 lg:grid-cols-${columnsCount}`}
        style={{
          gridAutoRows: '1fr',
        }}
      >
        {section_with_cards.map((card: any, cardIndex: number) => {
          // Determine alignment from card title or another property
          const alignment = card.alignment || "center"; // Default to "center" if not provided
          const alignmentClass =
            alignment === "left"
              ? "items-start text-left"
              : alignment === "right"
              ? "items-end text-right"
              : "items-center text-center";

          return (
            <div
              key={cardIndex}
              className={`card bg-[#F8F8F8] flex flex-col ${alignmentClass} justify-start max-w-[330px] ${card.additional_css || ''}`.trim()}
            >
              {card.image?.url && (
                <img
                  src={card.image.url}
                  alt={card.image.description}
                  className={`block mb-4 w-auto h-auto max-w-[150px] ${
                    alignment === "left"
                      ? "self-start"
                      : alignment === "right"
                      ? "self-end"
                      : "self-center"
                  }`}
                />
              )}
              {card.title && <Markdown markdown={card.title} className="text-18 font-bold leading-[1.5rem] text-primary-black md:text-0 lg:text-20 xl:text-20 2xl:text-22 relative" />}
              {card.description && (
                <p
                  className="whitespace-pre-wrap mt-10 font-normal text-primary-black text-14 md:text-16 lg:leading-8 2xl:text-16 leading-6"
                  dangerouslySetInnerHTML={{ __html: card.description }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
       // Check if the section is of type `multiple_image`
if (section.multiple_image) {
    const { section_with_images } = section.multiple_image;
  
    return (
      <div key={index} className="hero-slider-container w-full">
        <Slider
          slidesPerView={isTabletSize ? 1.3 : 1} // Adjust for responsive design
          dots={true}
          spaceBetween={16}
          arrows={isTabletSize}
          innerArrows
          allowArrowsOnTouchDevice
          allowTouchMove={true}
          prevButtonStyles={{ padding: '15px', left: '0px' }}
          nextButtonStyles={{ padding: '15px', right: '0px' }}
          autoplay={{
            delay: 2000,
            disableOnInteraction: false,
          }}
        >
          {section_with_images.image.map(
            (slide: { url: string | StaticImageData | undefined; title: string | undefined }, imgIndex: number) => (
              <div key={imgIndex} className="relative h-[18rem] md:h-[34rem] 2xl:h-[44rem]">
                {slide?.url && (
                  <Image
                    loading="lazy"
                    src={slide.url}
                    alt={slide.title || "Image"}
                    fill
                    style={{ objectFit: 'cover' }}
                  />
                )}
              </div>
            )
          )}
        </Slider>
      </div>
    );
  }
  

        return null; // Return null if no matching section type is found
      })}
    </div>
  );
};

export default ContentstackRenderer;